import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Guards against a race where the initial bootstrap /auth/me (fired before the
  // session cookie exists, so it 401s) resolves AFTER a login set the user, and
  // its catch would otherwise clobber the authenticated user back to null. Once
  // any successful auth has happened, a stale 401 must not wipe the session.
  const loggedInRef = useRef(false);

  // SEC-06 / D-2: the session lives in an httpOnly cookie the browser sends
  // automatically — there is no JS-readable token. We bootstrap auth state by
  // asking the backend who we are (/auth/me) using the cookie. A 401 simply
  // means "not signed in".
  const fetchUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      loggedInRef.current = true;
      setUser(res.data);
      return res.data;
    } catch (e) {
      // Only drop to signed-out if we were never authenticated in this session.
      if (!loggedInRef.current) setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: if returning from the Google OAuth callback (#session_id in the
    // URL fragment), skip the bootstrap /me check — GoogleAuthCallback exchanges
    // the session_id and establishes the session cookie first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, [fetchUser]);

  // login() is called after a successful auth call that set the session cookie.
  // We just record the returned user; the cookie carries the credential.
  const login = useCallback(async (_accessToken, userData) => {
    loggedInRef.current = true;
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    // Clear the httpOnly session + CSRF cookies server-side, then drop local state.
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // best-effort; clear local state regardless
    }
    loggedInRef.current = false;
    setUser(null);
  }, []);

  const requestMagicLink = useCallback(async (email, options = {}) => {
    const res = await api.post('/auth/magic-link/request', {
      email,
      app_origin: window.location.origin,
      // Slice 7.4: forward same-origin ?redirect= so the email link brings
      // the user back to the exact page they left (checkout, etc.).
      ...(options.redirect ? { redirect: options.redirect } : {}),
    });
    return res.data;
  }, []);

  const consumeMagicLink = useCallback(async (magicToken) => {
    const res = await api.post('/auth/magic-link/consume', { token: magicToken });
    const { access_token, user: userData } = res.data;
    await login(access_token, userData);
    return userData;
  }, [login]);

  const passwordLogin = useCallback(async (email, password) => {
    const res = await api.post('/auth/password/login', { email, password });
    const { access_token, user: userData } = res.data;
    await login(access_token, userData);
    return userData;
  }, [login]);

  const setInitialPassword = useCallback(async (password) => {
    await api.post('/account/password/set', { password });
    await fetchUser();
  }, [fetchUser]);

  const isStaff = user?.account_type === 'staff';
  const isFullAdmin = user?.staff_role === 'full_admin' || user?.role === 'admin';
  const isAdmin = isFullAdmin;
  const canAccessTeam = isStaff || isFullAdmin;
  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{
      user, loading, isAdmin, isStaff, isFullAdmin, canAccessTeam, isAuthenticated,
      login, logout, requestMagicLink, consumeMagicLink, passwordLogin, setInitialPassword, fetchUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
